import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import type { FpsParticleSystem } from './FpsParticleSystem.js';
import {
  formatRarityWeaponName,
  getStatMultipliers,
  type WeaponRarity,
} from './FpsWeaponRarity.js';
import {
  EXTENDED_MAG_MULT,
  getModSlot,
  getModsList,
  SCOPE_ADS_SPREAD_MULT,
  SILENCER_DAMAGE_MULT,
  type WeaponMod,
  type WeaponModSet,
} from './FpsWeaponMods.js';
import { bloomRecoverySpreadMult } from './matchRules.js';

export type WeaponKind = 'ar' | 'shotgun' | 'sniper' | 'smg' | 'pistol';

export function weaponKindShortLabel(kind: WeaponKind): string {
  switch (kind) {
    case 'ar':
      return 'AR';
    case 'shotgun':
      return 'SG';
    case 'sniper':
      return 'SR';
    case 'smg':
      return 'SMG';
    case 'pistol':
      return 'PI';
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

const WEAPON_NAMES: Record<WeaponKind, string> = {
  ar: 'Assault Rifle',
  shotgun: 'Pump Shotgun',
  sniper: 'Bolt Sniper',
  smg: 'SMG',
  pistol: 'Pistol',
};

const PROFILE: Record<
  WeaponKind,
  {
    cooldown: number;
    maxDist: number;
    impulse: number;
    rays: number;
    spread: number;
    damage: number;
    /** ADS FOV multiplier vs base ADS FOV (lower = more zoom). */
    adsFovMult: number;
  }
> = {
  ar: { cooldown: 0.105, maxDist: 130, impulse: 52, rays: 1, spread: 0.009, damage: 22, adsFovMult: 0.66 },
  shotgun: { cooldown: 0.52, maxDist: 46, impulse: 34, rays: 7, spread: 0.06, damage: 14, adsFovMult: 0.72 },
  sniper: { cooldown: 0.62, maxDist: 240, impulse: 92, rays: 1, spread: 0.0035, damage: 85, adsFovMult: 0.52 },
  smg: { cooldown: 0.058, maxDist: 72, impulse: 28, rays: 1, spread: 0.022, damage: 11, adsFovMult: 0.76 },
  pistol: { cooldown: 0.19, maxDist: 58, impulse: 36, rays: 1, spread: 0.014, damage: 26, adsFovMult: 0.74 },
};

/** Per-shot view recoil deltas (pitch up = negative, yaw = horizontal drift). */
const RECOIL_PATTERN: Record<WeaponKind, { pitch: readonly number[]; yaw: readonly number[] }> = {
  ar: {
    pitch: [-0.018, -0.018, -0.019, -0.018, -0.017, -0.015, -0.015, -0.013, -0.012, -0.011],
    yaw: [0, 0, 0.001, 0.002, 0.003, 0.003, 0.004, 0.004, 0.005, 0.005],
  },
  shotgun: { pitch: [-0.085], yaw: [0] },
  sniper: { pitch: [-0.062], yaw: [0.012] },
  smg: {
    pitch: [-0.011, -0.01, -0.012, -0.01, -0.011, -0.009, -0.01, -0.008],
    yaw: [0.008, -0.007, 0.009, -0.008, 0.007, -0.009, 0.008, -0.007],
  },
  pistol: { pitch: [-0.038], yaw: [0.006] },
};

const RELOAD_CANCEL_MIN_RATIO = 0.4;
const TACTICAL_RELOAD_MULT = 0.85;

/** SMG sustained fire overheats like a minigun. */
const OVERHEAT_WEAPON: WeaponKind = 'smg';
const HEAT_PER_SHOT = 0.042;
const HEAT_DECAY_PER_SEC = 0.38;
const OVERHEAT_THRESHOLD = 1;
const OVERHEAT_COOLDOWN = 2.4;

const DUAL_PISTOL_SPREAD = 0.038;
const DUAL_PISTOL_RAY_OFFSET = 0.11;
const FIRST_SHOT_IDLE_S = 0.3;
const FIRST_SHOT_SPREAD_MULT = 0.3;

/** Fixed pellet cone offsets (tan/bitangent normalized) — pump vs tac spread feel. */
const PUMP_SHOTGUN_PELLETS: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [-0.82, 0.38],
  [0.82, 0.38],
  [-0.48, -0.72],
  [0.48, -0.72],
  [-0.28, 0.88],
  [0.28, 0.88],
];

const TAC_SHOTGUN_PELLETS: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [-1.0, 0.55],
  [1.0, 0.55],
  [-0.72, -0.55],
  [0.72, -0.55],
  [-0.42, 0.95],
  [0.42, 0.95],
  [-0.18, -0.95],
  [0.18, -0.95],
  [-0.95, -0.18],
  [0.95, -0.18],
  [-0.55, 0.72],
  [0.55, 0.72],
];

function getShotgunPelletPattern(kind: WeaponKind): ReadonlyArray<readonly [number, number]> | null {
  if (kind !== 'shotgun') return null;
  return PROFILE.shotgun.rays >= 10 ? TAC_SHOTGUN_PELLETS : PUMP_SHOTGUN_PELLETS;
}

/** Per-class recoil step for the given shot index in the current burst. */
export function getWeaponRecoilForShot(
  kind: WeaponKind,
  shotIndex: number,
): { pitch: number; yaw: number; springSpeed: number } {
  const pattern = RECOIL_PATTERN[kind];
  const idx = Math.min(shotIndex, pattern.pitch.length - 1);
  const pitch = pattern.pitch[idx] ?? pattern.pitch[pattern.pitch.length - 1] ?? -0.015;
  const yaw = pattern.yaw[idx] ?? pattern.yaw[pattern.yaw.length - 1] ?? 0;
  const springSpeed =
    kind === 'shotgun' ? 22 : kind === 'sniper' ? 12 : kind === 'smg' ? 16 : kind === 'pistol' ? 18 : 14;
  return { pitch, yaw, springSpeed };
}

type MuzzleBloomProfile = {
  lightIntensity: number;
  lightDistance: number;
  flashOpacity: number;
  flashScale: [number, number];
  color: number;
  tracerColor: number;
  tracerRadius: number;
  tracerOpacity: number;
  tracerFadeMs: number;
};

const MUZZLE_BLOOM: Record<WeaponKind, MuzzleBloomProfile> = {
  ar: {
    lightIntensity: 10.5,
    lightDistance: 16,
    flashOpacity: 0.82,
    flashScale: [0.92, 0.72],
    color: 0xffdf9a,
    tracerColor: 0xfff0a8,
    tracerRadius: 0.014,
    tracerOpacity: 0.78,
    tracerFadeMs: 92,
  },
  shotgun: {
    lightIntensity: 13.5,
    lightDistance: 14,
    flashOpacity: 0.92,
    flashScale: [1.65, 1.12],
    color: 0xffa56b,
    tracerColor: 0xffba72,
    tracerRadius: 0.022,
    tracerOpacity: 0.72,
    tracerFadeMs: 105,
  },
  sniper: {
    lightIntensity: 16,
    lightDistance: 22,
    flashOpacity: 1,
    flashScale: [0.62, 1.55],
    color: 0xf4ffff,
    tracerColor: 0xd8ffff,
    tracerRadius: 0.012,
    tracerOpacity: 0.95,
    tracerFadeMs: 145,
  },
  smg: {
    lightIntensity: 9,
    lightDistance: 13,
    flashOpacity: 0.72,
    flashScale: [0.78, 0.62],
    color: 0xffe2a8,
    tracerColor: 0xffefb0,
    tracerRadius: 0.012,
    tracerOpacity: 0.7,
    tracerFadeMs: 76,
  },
  pistol: {
    lightIntensity: 8.5,
    lightDistance: 12,
    flashOpacity: 0.76,
    flashScale: [0.7, 0.66],
    color: 0xffe8b8,
    tracerColor: 0xffefbd,
    tracerRadius: 0.011,
    tracerOpacity: 0.74,
    tracerFadeMs: 88,
  },
};

const MAG_MAX: Record<WeaponKind, number> = {
  ar: 30,
  shotgun: 8,
  sniper: 5,
  smg: 25,
  pistol: 12,
};

const RELOAD_TIME: Record<WeaponKind, number> = {
  ar: 1.45,
  shotgun: 2.25,
  sniper: 2.65,
  smg: 1.35,
  pistol: 1.15,
};

/** AR/SMG line tracers only beyond this distance (m). */
const TRACER_MIN_RANGE = 15;

/** Listener invoked once per shot ray for bot hit detection, etc. */
export type ShotHitCallback = (info: {
  point: THREE.Vector3;
  object: THREE.Object3D;
  damage: number;
  dir: THREE.Vector3;
  origin: THREE.Vector3;
}) => void;

export class WeaponSystem {
  /** Holster + equip time — blocks fire and drives view-model swap animation. */
  static readonly SWAP_DURATION = 0.2;

  private raycaster = new THREE.Raycaster();
  private cooldown = 0;
  private muzzleLight: THREE.PointLight;
  private kind: WeaponKind = 'ar';
  private currentRarity: WeaponRarity = 'common';
  private mag: Record<WeaponKind, number> = { ...MAG_MAX };
  private reloadT = 0;
  private reloadingFor: WeaponKind | null = null;
  private swapEquip = 0;
  private adsActive = false;
  private sprinting = false;
  private sliding = false;
  private crosshairRecoilKick = 0;
  private timeSinceLastShot = FIRST_SHOT_IDLE_S;
  private reloadPulse = 0;
  private muzzleFlash: THREE.Mesh;
  private shotHitCb: ShotHitCallback | null = null;
  private extraHitTargets: () => THREE.Object3D[] = () => [];
  private currentModSet: WeaponModSet = {};
  private readonly weaponModSets = new Map<WeaponKind, WeaponModSet>();
  private altFireActive = false;
  private weaponHeat = 0;
  private overheatCooldown = 0;
  private dualWieldShotToggle = false;
  private particleSystem: FpsParticleSystem | null = null;

  constructor(
    private camera: THREE.Camera,
    private scene: THREE.Scene,
    private hitTargets: THREE.Object3D[],
    private impulseStrength = 48,
  ) {
    this.muzzleLight = new THREE.PointLight(0xffcc88, 0, 10);
    this.muzzleLight.castShadow = false;

    // Sprite-like muzzle flash plane attached to camera
    this.muzzleFlash = new THREE.Mesh(
      new THREE.PlaneGeometry(0.42, 0.42),
      new THREE.MeshBasicMaterial({
        color: 0xffe6a0,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    this.muzzleFlash.position.set(0.06, -0.04, -0.6);
    this.muzzleFlash.rotation.z = Math.random() * Math.PI * 2;
  }

  /** Returns true when the active weapon kind actually changed. */
  setWeaponKind(k: WeaponKind): boolean {
    if (k === this.kind) return false;
    if (this.reloadT > 0 && this.reloadingFor === this.kind && this.getReloadRatio() >= RELOAD_CANCEL_MIN_RATIO) {
      this.cancelReload();
    }
    this.kind = k;
    this.syncCurrentModSetFromKind();
    this.swapEquip = WeaponSystem.SWAP_DURATION;
    return true;
  }

  /** Scoped FOV scale for the active weapon (1 = default ADS FOV). */
  getAdsFovMultiplier(): number {
    return PROFILE[this.kind].adsFovMult;
  }

  getRecoilForShot(shotIndex: number): { pitch: number; yaw: number; springSpeed: number } {
    return getWeaponRecoilForShot(this.kind, shotIndex);
  }

  getCurrentModSet(): WeaponModSet {
    return this.currentModSet;
  }

  /** @deprecated Prefer getCurrentModSet — returns first attached mod if any. */
  getCurrentMod(): WeaponMod | null {
    return this.currentModSet.slotA ?? this.currentModSet.slotB ?? null;
  }

  hasEmptyModSlot(): boolean {
    const set = this.getActiveModSet();
    return !set.slotA || !set.slotB;
  }

  canApplyMod(mod: WeaponMod): boolean {
    const set = this.getActiveModSet();
    const slot = getModSlot(mod.kind);
    if (slot === 'B') return !set.slotB;
    if (!set.slotA) return true;
    return set.slotA.kind !== mod.kind;
  }

  /** Attach a mod to the active weapon in the correct slot. Returns true if applied. */
  applyMod(mod: WeaponMod): boolean {
    if (!this.canApplyMod(mod)) return false;
    const set = { ...(this.weaponModSets.get(this.kind) ?? {}) };
    const slot = getModSlot(mod.kind);
    if (slot === 'B') {
      set.slotB = mod;
    } else {
      set.slotA = mod;
    }
    this.weaponModSets.set(this.kind, set);
    this.currentModSet = set;
    if (mod.kind === 'extended_mag') {
      const baseMax = MAG_MAX[this.kind];
      const newMax = this.getMagMaxForKind(this.kind);
      if (this.mag[this.kind] >= baseMax) {
        this.mag[this.kind] = newMax;
      } else {
        this.mag[this.kind] = Math.min(newMax, this.mag[this.kind]);
      }
    }
    return true;
  }

  clearMod(): void {
    this.clearModForKind(this.kind);
  }

  clearModForKind(kind: WeaponKind): void {
    const set = this.weaponModSets.get(kind);
    if (!set) return;
    const hadExtended = set.slotB?.kind === 'extended_mag';
    this.weaponModSets.delete(kind);
    if (kind === this.kind) this.currentModSet = {};
    if (hadExtended) {
      this.mag[kind] = Math.min(this.mag[kind], MAG_MAX[kind]);
    }
  }

  private syncCurrentModSetFromKind(): void {
    this.currentModSet = { ...(this.weaponModSets.get(this.kind) ?? {}) };
  }

  private getActiveModSet(): WeaponModSet {
    return this.weaponModSets.get(this.kind) ?? {};
  }

  private hasAttachedMod(kind: WeaponMod['kind']): boolean {
    const set = this.getActiveModSet();
    if (kind === 'extended_mag') return set.slotB?.kind === 'extended_mag';
    return set.slotA?.kind === kind;
  }

  private getMagMaxForKind(kind: WeaponKind): number {
    const set = this.weaponModSets.get(kind);
    const base = MAG_MAX[kind];
    if (set?.slotB?.kind === 'extended_mag') return Math.round(base * EXTENDED_MAG_MULT);
    return base;
  }

  private getActiveMagMax(): number {
    return this.getMagMaxForKind(this.kind);
  }

  getInspectStats(): {
    displayName: string;
    rarity: WeaponRarity;
    damage: number;
    fireRate: number;
    mods: WeaponMod[];
  } {
    const p = PROFILE[this.kind];
    const { damageMult, cooldownMult } = this.rarityMults();
    const silenced = this.hasAttachedMod('silencer');
    const damage = Math.round(p.damage * damageMult * (silenced ? SILENCER_DAMAGE_MULT : 1));
    const fireRate = Math.round((1 / (p.cooldown * cooldownMult)) * 10) / 10;
    return {
      displayName: this.getDisplayName(),
      rarity: this.currentRarity,
      damage,
      fireRate,
      mods: getModsList(this.getActiveModSet()),
    };
  }

  isSwapping(): boolean {
    return this.swapEquip > 0;
  }

  getSwapEquipRemaining(): number {
    return this.swapEquip;
  }

  setCombatState(state: { ads: boolean; sprinting: boolean }): void {
    this.adsActive = state.ads;
    this.sprinting = state.sprinting;
  }

  /** Right-click / alt-fire — dual pistols while held on pistol. */
  setAltFire(on: boolean): void {
    this.altFireActive = on;
  }

  isDualWieldActive(): boolean {
    return this.kind === 'pistol' && this.altFireActive;
  }

  /** 0–1 heat fill for SMG/minigun-style weapons; 0 when not applicable. */
  getHeatRatio(): number {
    if (this.kind !== OVERHEAT_WEAPON) return 0;
    return Math.max(0, Math.min(1, this.weaponHeat));
  }

  isOverheated(): boolean {
    return this.overheatCooldown > 0;
  }

  setSliding(on: boolean): void {
    this.sliding = on;
  }

  /** Hook in dynamic hit targets (e.g. bots). */
  setExtraHitTargetsProvider(fn: () => THREE.Object3D[]): void {
    this.extraHitTargets = fn;
  }

  setShotHitCallback(cb: ShotHitCallback | null): void {
    this.shotHitCb = cb;
  }

  /** 0–1 fill while current weapon is reloading. */
  getReloadRatio(): number {
    if (this.reloadT <= 0 || this.reloadingFor !== this.kind || !this.reloadingFor) return 0;
    const { cooldownMult } = this.rarityMults();
    const total = RELOAD_TIME[this.reloadingFor] * cooldownMult;
    return 1 - this.reloadT / total;
  }

  /** Alias for HUD reload ring progress. */
  getReloadProgress(): number {
    return this.getReloadRatio();
  }

  getAmmoHudText(): string {
    if (this.reloadT > 0.02 && this.reloadingFor === this.kind) return 'RELOAD';
    const max = this.getActiveMagMax();
    return `${this.mag[this.kind]}/${max}`;
  }

  private startReloadFor(kind: WeaponKind): void {
    if (this.reloadT > 0) return;
    if (this.mag[kind] >= this.getMagMaxForKind(kind)) return;
    this.reloadingFor = kind;
    const { cooldownMult } = this.rarityMults();
    const tactical = this.mag[kind] > 0;
    const speedMult = tactical ? TACTICAL_RELOAD_MULT : 1;
    this.reloadT = RELOAD_TIME[kind] * cooldownMult * speedMult;
  }

  /** Abort an in-progress reload (mag stays as-is). */
  cancelReload(): void {
    this.reloadT = 0;
    this.reloadingFor = null;
  }

  isReloading(): boolean {
    return this.reloadT > 0;
  }

  /** Manual reload (R key). Returns true if a reload was started. */
  tryReload(): boolean {
    if (this.reloadT > 0) return false;
    if (this.mag[this.kind] >= this.getActiveMagMax()) return false;
    this.startReloadFor(this.kind);
    return this.reloadT > 0;
  }

  /** Add ammo to the active weapon magazine; returns amount actually added. */
  addAmmo(amount: number): number {
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    const max = this.getActiveMagMax();
    const before = this.mag[this.kind];
    this.mag[this.kind] = Math.min(max, before + Math.round(amount));
    return this.mag[this.kind] - before;
  }

  /** Refill a weapon magazine to max (e.g. vending slot 0). Returns true if any ammo was added. */
  refillMagForKind(kind: WeaponKind): boolean {
    const before = this.mag[kind];
    this.mag[kind] = this.getMagMaxForKind(kind);
    if (this.reloadingFor === kind) {
      this.reloadT = 0;
      this.reloadingFor = null;
    }
    return this.mag[kind] > before;
  }

  getWeaponKind(): WeaponKind {
    return this.kind;
  }

  getRarity(): WeaponRarity {
    return this.currentRarity;
  }

  setRarity(r: WeaponRarity): void {
    this.currentRarity = r;
  }

  getDisplayName(): string {
    return formatRarityWeaponName(this.currentRarity, WEAPON_NAMES[this.kind]);
  }

  private rarityMults(): { damageMult: number; cooldownMult: number } {
    return getStatMultipliers(this.currentRarity);
  }

  /** 0–1 style kick added to crosshair spread; decays after each shot. */
  getCrosshairRecoilKick(): number {
    return this.crosshairRecoilKick;
  }

  /** 1.0 idle → ~1.8 at full tactical sprint; tighter while ADS; bloomed while jumping. */
  getCrosshairSpreadScale(horizSpeed: number, jumping = false): number {
    if (this.adsActive) return this.sliding ? 0.82 : 0.72;
    const maxRun = 13.6;
    const speedT = Math.min(1, Math.max(0, horizSpeed / maxRun));
    const moveAdd = speedT * 0.55;
    const sprintAdd = this.sprinting ? 0.25 : 0;
    const slideMul = this.sliding ? 1.8 : 1;
    const jumpMul = jumping ? 2.25 : 1.0;
    return (1 + moveAdd + sprintAdd) * slideMul * jumpMul;
  }

  /** Wider line gap for shotgun spread feel. */
  getCrosshairGapMultiplier(): number {
    return this.kind === 'shotgun' ? 1.65 : 1;
  }

  dispose(): void {
    this.muzzleLight.dispose();
    this.muzzleFlash.geometry.dispose();
    (this.muzzleFlash.material as THREE.Material).dispose();
  }

  attachMuzzle(parent: THREE.Object3D): void {
    parent.add(this.muzzleLight);
    this.muzzleLight.position.set(0.06, -0.04, -0.55);
    parent.add(this.muzzleFlash);
    this.muzzleFlash.layers.set(1);
  }

  setParticleSystem(particles: FpsParticleSystem): void {
    this.particleSystem = particles;
  }

  update(dt: number): void {
    this.cooldown = Math.max(0, this.cooldown - dt);
    this.swapEquip = Math.max(0, this.swapEquip - dt);
    if (this.overheatCooldown > 0) {
      this.overheatCooldown = Math.max(0, this.overheatCooldown - dt);
      if (this.overheatCooldown <= 0) this.weaponHeat = 0;
    } else if (this.kind === OVERHEAT_WEAPON && this.weaponHeat > 0) {
      this.weaponHeat = Math.max(0, this.weaponHeat - HEAT_DECAY_PER_SEC * dt);
    }
    this.muzzleLight.intensity *= 0.78;
    const mfMat = this.muzzleFlash.material as THREE.MeshBasicMaterial;
    mfMat.opacity *= 0.6;
    this.timeSinceLastShot += dt;
    if (this.timeSinceLastShot >= FIRST_SHOT_IDLE_S) {
      this.crosshairRecoilKick = 0;
    } else {
      this.crosshairRecoilKick = THREE.MathUtils.lerp(
        this.crosshairRecoilKick,
        0,
        Math.min(1, 14 * dt),
      );
    }
    if (this.reloadT > 0) {
      this.reloadT = Math.max(0, this.reloadT - dt);
      if (this.reloadT <= 0 && this.reloadingFor) {
        this.mag[this.reloadingFor] = this.getMagMaxForKind(this.reloadingFor);
        this.reloadingFor = null;
        this.reloadPulse = 0.22;
        const mfMat = this.muzzleFlash.material as THREE.MeshBasicMaterial;
        mfMat.opacity = 0.42;
        this.muzzleLight.intensity = 4.5;
        this.muzzleLight.color.setHex(0xaaccff);
      }
    }
    if (this.reloadPulse > 0) {
      this.reloadPulse = Math.max(0, this.reloadPulse - dt);
    }
  }

  /** Floor loot / ammo pads — trims time until next shot. */
  shrinkCooldown(seconds: number): void {
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    this.cooldown = Math.max(0, this.cooldown - seconds);
    this.reloadT = Math.max(0, this.reloadT - seconds * 0.35);
  }

  /** Returns true if shot fired */
  fire(): boolean {
    if (this.swapEquip > 0) return false;
    if (this.kind === OVERHEAT_WEAPON && this.overheatCooldown > 0) return false;
    if (this.reloadT > 0) {
      if (this.getReloadRatio() >= RELOAD_CANCEL_MIN_RATIO) {
        this.cancelReload();
      } else {
        return false;
      }
    }
    if (this.mag[this.kind] <= 0) {
      this.startReloadFor(this.kind);
      return false;
    }
    if (this.cooldown > 0) return false;
    const p = PROFILE[this.kind];
    const dualWield = this.isDualWieldActive();
    const { damageMult, cooldownMult } = this.rarityMults();
    const modDamageMult = this.hasAttachedMod('silencer') ? SILENCER_DAMAGE_MULT : 1;
    const paceMul = dualWield ? 0.88 : this.adsActive ? 0.92 : this.sprinting ? 1.14 : 1;
    this.cooldown = p.cooldown * paceMul * cooldownMult;
    this.mag[this.kind] -= 1;
    if (this.kind === OVERHEAT_WEAPON) {
      this.weaponHeat = Math.min(OVERHEAT_THRESHOLD, this.weaponHeat + HEAT_PER_SHOT);
      if (this.weaponHeat >= OVERHEAT_THRESHOLD) {
        this.overheatCooldown = OVERHEAT_COOLDOWN;
      }
    }
    const baseDir = this.camera.getWorldDirection(new THREE.Vector3()).normalize();
    const tan = new THREE.Vector3(0, 1, 0).cross(baseDir);
    if (tan.lengthSq() < 1e-6) tan.set(1, 0, 0);
    tan.normalize();
    const bitan = new THREE.Vector3().crossVectors(baseDir, tan).normalize();

    const bloom = MUZZLE_BLOOM[this.kind];
    const rareWarm = this.currentRarity === 'legendary';
    const rarityBoost = rareWarm ? 1.16 : 1;
    const muzzleColor = rareWarm ? 0xffb15a : bloom.color;
    this.muzzleLight.intensity = bloom.lightIntensity * rarityBoost;
    this.muzzleLight.distance = bloom.lightDistance;
    this.muzzleLight.color.setHex(muzzleColor);

    const silenced = this.hasAttachedMod('silencer');
    if (!silenced) {
      const mfMat = this.muzzleFlash.material as THREE.MeshBasicMaterial;
      mfMat.color.setHex(muzzleColor);
      mfMat.opacity = Math.min(1, bloom.flashOpacity * rarityBoost);
      this.muzzleFlash.rotation.z = Math.random() * Math.PI * 2;
      this.muzzleFlash.scale.set(bloom.flashScale[0], bloom.flashScale[1], 1);
    } else {
      this.muzzleLight.intensity = 0;
      const mfMat = this.muzzleFlash.material as THREE.MeshBasicMaterial;
      mfMat.opacity = 0;
    }

    const impulse = p.impulse > 0 ? p.impulse : this.impulseStrength;
    const origin = this.camera.getWorldPosition(new THREE.Vector3());

    // Build combined hit-target list once
    const targets: THREE.Object3D[] = this.hitTargets.slice();
    for (const t of this.extraHitTargets()) targets.push(t);

    // Phase 32X-4: bloom recovery curve (pure rule) for first-shot accuracy
    const accuracyMul = dualWield
      ? 1
      : bloomRecoverySpreadMult(this.timeSinceLastShot, FIRST_SHOT_IDLE_S, FIRST_SHOT_SPREAD_MULT);
    this.timeSinceLastShot = 0;
    const pelletPattern = dualWield ? null : getShotgunPelletPattern(this.kind);
    const rayCount = dualWield ? 2 : pelletPattern ? pelletPattern.length : p.rays;

    for (let r = 0; r < rayCount; r++) {
      const dir = baseDir.clone();
      const spreadMul = dualWield
        ? 1.35
        : this.adsActive
          ? (this.sliding ? 0.58 : 0.46) *
            (this.hasAttachedMod('scope') ? SCOPE_ADS_SPREAD_MULT : 1)
          : this.sliding
            ? 1.8
            : this.sprinting
              ? 1.9
              : 1;
      const spread = dualWield ? DUAL_PISTOL_SPREAD : p.spread * spreadMul * accuracyMul;
      if (spread > 0) {
        if (pelletPattern) {
          const [u, v] = pelletPattern[r] ?? [0, 0];
          dir.addScaledVector(tan, u * spread);
          dir.addScaledVector(bitan, v * spread);
        } else {
          const sideBias = dualWield ? (r === 0 ? -DUAL_PISTOL_RAY_OFFSET : DUAL_PISTOL_RAY_OFFSET) : 0;
          dir.addScaledVector(tan, sideBias + (Math.random() - 0.5) * spread * 2);
          dir.addScaledVector(bitan, (Math.random() - 0.5) * spread * 2);
        }
        dir.normalize();
      }
      this.raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera);
      this.raycaster.ray.origin.copy(origin);
      this.raycaster.ray.direction.copy(dir);
      this.raycaster.far = p.maxDist;
      this.raycaster.layers.set(0);
      const hits = this.raycaster.intersectObjects(targets, true);
      let hitPoint: THREE.Vector3;
      if (hits.length === 0) {
        hitPoint = origin.clone().add(dir.clone().multiplyScalar(p.maxDist));
        this.spawnTracer(origin, hitPoint);
        continue;
      }
      const hit = hits[0]!;
      const pt = hit.point.clone();
      this.spawnTracer(origin, pt);
      this.spawnImpact(pt, hit.face?.normal);

      // Distance-based damage drop-off
      const dist = origin.distanceTo(pt);
      let falloff = 1;
      if (dist > p.maxDist * 0.4) {
        // Falloff starts at 40% of max range
        const f = (dist - p.maxDist * 0.4) / (p.maxDist * 0.6);
        falloff = Math.max(0.4, 1 - f * 0.6); // Can drop down to 40% damage
      }

      // Try bot/object hit reporting
      this.shotHitCb?.({
        point: pt,
        object: hit.object,
        damage: Math.round(p.damage * damageMult * modDamageMult * falloff),
        dir,
        origin,
      });

      let obj: THREE.Object3D | null = hit.object;
      while (obj) {
        const body = obj.userData.cannonBody as CANNON.Body | undefined;
        if (body && body.mass > 0) {
          body.applyImpulse(
            new CANNON.Vec3(dir.x * impulse, dir.y * 0.18 * impulse, dir.z * impulse),
            new CANNON.Vec3(pt.x, pt.y, pt.z),
          );
          break;
        }
        obj = obj.parent;
      }
    }
    if (this.mag[this.kind] <= 0) {
      this.startReloadFor(this.kind);
    }
    const kickStrength =
      this.kind === 'shotgun'
        ? 0.161
        : this.kind === 'sniper'
          ? 0.09
          : this.kind === 'ar'
            ? 0.075
            : 0.065;
    this.crosshairRecoilKick = Math.max(
      this.crosshairRecoilKick,
      dualWield ? kickStrength * 1.12 : kickStrength,
    );
    if (dualWield) this.dualWieldShotToggle = !this.dualWieldShotToggle;
    return true;
  }

  getDualWieldShotToggle(): boolean {
    return this.dualWieldShotToggle;
  }

  /** Thin glowing tracer from muzzle origin to hit/miss point. */
  private spawnTracer(origin: THREE.Vector3, end: THREE.Vector3): void {
    const dir = end.clone().sub(origin);
    const len = dir.length();
    if (len < 0.2) return;
    const bloom = MUZZLE_BLOOM[this.kind];
    const rareWarm = this.currentRarity === 'legendary';
    const tracerOpacity = Math.min(1, bloom.tracerOpacity * (rareWarm ? 1.12 : 1));
    const tracerColor = rareWarm ? 0xffc069 : bloom.tracerColor;

    if (
      (this.kind === 'ar' || this.kind === 'smg') &&
      len >= TRACER_MIN_RANGE &&
      this.particleSystem
    ) {
      this.particleSystem.spawnBulletTracer(origin, end, tracerColor, tracerOpacity, bloom.tracerFadeMs);
      return;
    }

    const geo = new THREE.CylinderGeometry(bloom.tracerRadius, bloom.tracerRadius, len, 5);
    const mat = new THREE.MeshBasicMaterial({
      color: tracerColor,
      transparent: true,
      opacity: tracerOpacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    const mid = origin.clone().add(end).multiplyScalar(0.5);
    mesh.position.copy(mid);
    mesh.lookAt(end);
    mesh.rotateX(Math.PI / 2);
    this.scene.add(mesh);
    const t0 = performance.now();
    const fade = () => {
      const t = (performance.now() - t0) / bloom.tracerFadeMs;
      mat.opacity = Math.max(0, tracerOpacity * (1 - t));
      if (t < 1) requestAnimationFrame(fade);
      else {
        geo.dispose();
        mat.dispose();
        this.scene.remove(mesh);
      }
    };
    requestAnimationFrame(fade);
  }

  private spawnImpact(pos: THREE.Vector3, normal?: THREE.Vector3): void {
    const orient = (mesh: THREE.Mesh) => {
      mesh.position.copy(pos);
      if (normal) {
        const nn = normal.clone().normalize();
        mesh.lookAt(pos.clone().add(nn));
      } else {
        mesh.lookAt(pos.clone().add(this.camera.getWorldDirection(new THREE.Vector3())));
      }
    };

    const mkRing = (inner: number, outer: number, color: number, opacity: number) => {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(inner, outer, 18),
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity,
          side: THREE.DoubleSide,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      orient(ring);
      this.scene.add(ring);
      return ring;
    };

    const ringA = mkRing(0.04, 0.28, 0x55ffff, 0.85);
    const ringB = mkRing(0.12, 0.38, 0xffee55, 0.65);

    const sparks: THREE.Mesh[] = [];
    const dir = this.camera.getWorldDirection(new THREE.Vector3()).normalize();
    const tan = new THREE.Vector3(0, 1, 0).cross(dir);
    if (tan.lengthSq() < 0.001) tan.set(1, 0, 0);
    tan.normalize();
    const bitan = new THREE.Vector3().crossVectors(dir, tan).normalize();
    for (let i = 0; i < 10; i++) {
      const chip = new THREE.Mesh(
        new THREE.TetrahedronGeometry(0.045 + Math.random() * 0.04, 0),
        new THREE.MeshBasicMaterial({
          color: i % 2 === 0 ? 0x66ffff : 0xffaa44,
          transparent: true,
          opacity: 0.95,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      chip.position.copy(pos);
      chip.userData.vel = dir
        .clone()
        .multiplyScalar(0.4 + Math.random() * 0.5)
        .add(tan.clone().multiplyScalar((Math.random() - 0.5) * 0.35))
        .add(bitan.clone().multiplyScalar((Math.random() - 0.5) * 0.35))
        .add(
          new THREE.Vector3(
            (Math.random() - 0.5) * 0.12,
            (Math.random() - 0.5) * 0.12,
            (Math.random() - 0.5) * 0.12,
          ),
        );
      this.scene.add(chip);
      sparks.push(chip);
    }

    const t0 = performance.now();
    const fade = () => {
      const t = (performance.now() - t0) / 280;
      ringA.scale.setScalar(1 + t * 2.8);
      ringB.scale.setScalar(1 + t * 2.2);
      (ringA.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.85 - t * 1.1);
      (ringB.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.65 - t * 0.95);
      const dt = 0.016;
      for (const s of sparks) {
        const v = s.userData.vel as THREE.Vector3;
        s.position.addScaledVector(v, dt);
        v.y -= 1.2 * dt;
        (s.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.95 - t * 1.4);
      }
      if (t < 1) requestAnimationFrame(fade);
      else {
        ringA.geometry.dispose();
        (ringA.material as THREE.Material).dispose();
        ringB.geometry.dispose();
        (ringB.material as THREE.Material).dispose();
        this.scene.remove(ringA, ringB);
        for (const s of sparks) {
          s.geometry.dispose();
          (s.material as THREE.Material).dispose();
          this.scene.remove(s);
        }
      }
    };
    requestAnimationFrame(fade);
  }
}
